import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { residentialServices, commercialServices } from '../mockData';
import { Home, Sparkles, Truck, Building2, Layers, Square, HardHat, Check, Key, Store, Stethoscope, Baby, ArrowLeft } from 'lucide-react';

const iconMap = {
  Home,
  Sparkles,
  Truck,
  Building2,
  Layers,
  Square,
  HardHat,
  Key,
  Store,
  Stethoscope,
  Baby
};

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const serviceImages = {
    'regular-cleaning': 'https://images.unsplash.com/photo-1527515637462-cff94eecc1ac',
    'deep-cleaning': 'https://images.unsplash.com/photo-1581578731548-c64695cc6952',
    'move-cleaning': 'https://images.unsplash.com/photo-1603712725038-e9334ae8f39f',
    'airbnb-cleaning': 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267',
    'office-cleaning': 'https://images.unsplash.com/photo-1627905646269-7f034dcc5738',
    'retail-cleaning': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8',
    'daycare-cleaning': 'https://images.unsplash.com/photo-1587654780291-39c9404d746b',
    'medical-cleaning': 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d',
    'construction-cleaning': 'https://images.unsplash.com/photo-1563453392212-326f5e854473'
  };

  const ServiceCard = ({ service, isCommercial }) => {
    const IconComponent = iconMap[service.icon] || Building2;
    const borderColor = isCommercial ? 'hover:border-yellow-400' : 'hover:border-blue-400';
    const buttonColor = isCommercial 
      ? 'bg-yellow-500 hover:bg-yellow-600 text-gray-900' 
      : 'bg-blue-600 hover:bg-blue-700 text-white';
    
    return (
      <Card className={`hover:shadow-xl transition-all duration-300 border-2 ${borderColor} overflow-hidden`}>
        <div className="h-48 overflow-hidden relative">
          <img 
            src={serviceImages[service.id] || 'https://images.unsplash.com/photo-1581578731548-c64695cc6952'} 
            alt={service.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-4 left-4">
            <div className={`w-12 h-12 ${isCommercial ? 'bg-yellow-400' : 'bg-white'} rounded-lg flex items-center justify-center`}>
              <IconComponent className={`w-6 h-6 ${isCommercial ? 'text-gray-900' : 'text-blue-600'}`} />
            </div>
          </div>
        </div>
        <CardHeader>
          <CardTitle className="text-2xl mb-2">{service.name}</CardTitle>
          <CardDescription className="text-base">{service.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <h4 className="font-semibold mb-3 text-gray-900">What&apos;s Included:</h4>
            <ul className="space-y-2">
              {service.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3 text-gray-900">Starting From:</h4>
            <div className="flex items-baseline gap-3">
              <p className={`text-3xl font-bold ${isCommercial ? 'text-yellow-600' : 'text-blue-600'}`}>
                ${service.pricing.oneTime}
              </p>
              <span className="text-sm text-gray-500">one-time</span>
            </div>
            {service.pricing.monthly && (
              <div className="flex items-baseline gap-3 mt-1">
                <p className="text-lg font-bold text-green-600">
                  ${service.pricing.weekly}
                </p>
                <span className="text-xs text-green-600">weekly</span>
                <p className="text-lg font-bold text-green-600">
                  ${service.pricing.biWeekly}
                </p>
                <span className="text-xs text-green-600">bi-weekly</span>
                <p className="text-lg font-bold text-green-600">
                  ${service.pricing.monthly}
                </p>
                <span className="text-xs text-green-600">monthly</span>
              </div>
            )}
            {!service.pricing.monthly && (
              <p className="text-sm text-gray-500 mt-1 italic">One-time service only</p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              + ${service.pricing.bedroom || service.pricing.room}/{isCommercial ? 'room' : 'bedroom'} 
              + ${service.pricing.bathroom}/{isCommercial ? 'restroom' : 'bathroom'}
            </p>
          </div>
          <Link to="/booking" state={{ selectedService: service.id, selectedCategory: service.category }}>
            <Button className={`w-full mt-6 ${buttonColor} font-semibold`}>
              Book This Service
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  };

  // Category Selection Screen
  if (!selectedCategory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-4 text-gray-900">Our Cleaning Services</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select your service type to view available cleaning options
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Residential Option */}
              <div
                onClick={() => setSelectedCategory('residential')}
                className="cursor-pointer group"
              >
                <Card className="h-full border-2 border-gray-200 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="h-48 relative overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1758273238370-3bc08e399620?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800" alt="Residential Cleaning" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-blue-900/40"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center">
                        <Home className="w-10 h-10 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader className="text-center">
                    <CardTitle className="text-3xl text-gray-900">Residential Cleaning</CardTitle>
                    <CardDescription className="text-lg">
                      Professional cleaning for homes & apartments
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <ul className="text-gray-600 space-y-2 mb-6">
                      <li>• Regular House Cleaning</li>
                      <li>• Deep Cleaning</li>
                      <li>• Move In/Out Cleaning</li>
                      <li>• Airbnb/Rental Turnover</li>
                    </ul>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg">
                      View Residential Services
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Commercial Option */}
              <div
                onClick={() => setSelectedCategory('commercial')}
                className="cursor-pointer group"
              >
                <Card className="h-full border-2 border-gray-200 hover:border-yellow-500 hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="h-48 relative overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1770992176317-382f51aff35d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800" alt="Commercial Cleaning" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-yellow-900/40"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center">
                        <Building2 className="w-10 h-10 text-yellow-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader className="text-center">
                    <CardTitle className="text-3xl text-gray-900">Commercial Cleaning</CardTitle>
                    <CardDescription className="text-lg">
                      Professional cleaning for businesses & offices
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <ul className="text-gray-600 space-y-2 mb-6">
                      <li>• Office Cleaning</li>
                      <li>• Retail Store Cleaning</li>
                      <li>• Daycare Cleaning</li>
                      <li>• Medical/Dental Office</li>
                    </ul>
                    <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-6 text-lg">
                      View Commercial Services
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 text-center">
            <Card className="max-w-2xl mx-auto bg-gradient-to-br from-gray-800 to-gray-900 border-0">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Not Sure Which Service You Need?</CardTitle>
                <CardDescription className="text-gray-300 text-base">
                  Contact us for a free consultation and personalized quote
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/contact">
                  <Button size="lg" className="bg-white hover:bg-gray-100 text-gray-900 font-semibold">
                    Contact Us
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Services List Screen (after category selection)
  const isCommercial = selectedCategory === 'commercial';
  const services = isCommercial ? commercialServices : residentialServices;
  const categoryTitle = isCommercial ? 'Commercial Cleaning' : 'Residential Cleaning';
  const categoryDesc = isCommercial 
    ? 'Professional cleaning for businesses and offices' 
    : 'Professional cleaning for homes and apartments';

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Back Button & Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCategory(null)}
            className="mb-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Categories
          </Button>
          
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 ${isCommercial ? 'bg-yellow-500' : 'bg-blue-600'} rounded-xl flex items-center justify-center`}>
              {isCommercial ? (
                <Building2 className="w-8 h-8 text-white" />
              ) : (
                <Home className="w-8 h-8 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{categoryTitle}</h1>
              <p className="text-lg text-gray-600">{categoryDesc}</p>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className={`grid md:grid-cols-2 ${isCommercial ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6`}>
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} isCommercial={isCommercial} />
          ))}
        </div>

        {/* Switch Category CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Looking for {isCommercial ? 'residential' : 'commercial'} cleaning instead?
          </p>
          <Button 
            variant="outline"
            onClick={() => setSelectedCategory(isCommercial ? 'residential' : 'commercial')}
            className={`${isCommercial ? 'border-blue-500 text-blue-600 hover:bg-blue-50' : 'border-yellow-500 text-yellow-600 hover:bg-yellow-50'}`}
          >
            View {isCommercial ? 'Residential' : 'Commercial'} Services
          </Button>
        </div>

        {/* Custom Quote CTA */}
        <div className="mt-12 text-center">
          <Card className={`max-w-2xl mx-auto border-0 ${isCommercial ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' : 'bg-gradient-to-br from-blue-600 to-blue-700'}`}>
            <CardHeader>
              <CardTitle className={`text-3xl ${isCommercial ? 'text-gray-900' : 'text-white'}`}>Need a Custom Package?</CardTitle>
              <CardDescription className={isCommercial ? 'text-gray-800 text-base' : 'text-blue-100 text-base'}>
                Contact us for a personalized quote tailored to your specific needs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/contact">
                <Button size="lg" className={isCommercial ? 'bg-gray-900 hover:bg-gray-800 text-white' : 'bg-yellow-500 hover:bg-yellow-600 text-gray-900'}>
                  Get Custom Quote
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Services;
